import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  Eye,
  Code,
  Copy,
  Check,
  Tag,
  Calendar,
  User,
  ChevronsDown,
  ChevronsUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useCopy } from '@/hooks/useCopy';
import { useMapStore } from '@/stores/mapStore';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CorrectionMode = 'RELOCATE' | 'SKETCH';

export interface FieldCorrection {
  id: string;
  correctionType: CorrectionMode;
  status: 'APPROVED' | 'REJECTED' | 'PENDING';
  proposedGeom: any;
  reviewNotes?: string | null;
  createdAt: string | Date;
  submittedBy?: { email: string } | null;
}

interface CorrectionTimelineProps {
  corrections: FieldCorrection[];
  onEdit?: (correction: FieldCorrection) => void;
  onHover?: (correctionId: string | null) => void; // NEW
  onFocus?: (correction: FieldCorrection) => void; // NEW
}

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  APPROVED: {
    bg: 'bg-green-500/10 text-green-500 border-green-500/20',
    icon: CheckCircle2,
  },
  REJECTED: {
    bg: 'bg-red-500/10 text-red-500 border-red-500/20',
    icon: XCircle,
  },
  PENDING: {
    bg: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    icon: Clock,
  },
} as const;

const PREVIEW_COUNT = 3;

// ─── GeoJsonPreview ───────────────────────────────────────────────────────────

export const GeoJsonPreview = ({
  data,
  onCopy,
  copied,
}: {
  data: any;
  onCopy: (t: string) => void;
  copied: boolean;
}) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        type="button"
        className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground h-7 rounded-md hover:bg-muted transition-colors px-2"
      >
        <Code className="w-3 h-3" /> Preview GeoJSON
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl bg-card">
      <DialogHeader>
        <div className="flex items-center justify-between pr-8">
          <DialogTitle>GeoJSON Preview</DialogTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopy(JSON.stringify(data, null, 2))}
          >
            {copied ? (
              <>
                Copied <Check className="ml-1.5 w-3 h-3" />
              </>
            ) : (
              <>
                Copy <Copy className="ml-1.5 w-3 h-3" />
              </>
            )}
          </Button>
        </div>
      </DialogHeader>
      <div className="bg-zinc-950 dark:bg-zinc-900 border border-border p-4 rounded-lg overflow-auto max-h-[480px]">
        <code className="text-xs text-emerald-400 font-mono whitespace-pre-wrap">
          {JSON.stringify(data, null, 2)}
        </code>
      </div>
    </DialogContent>
  </Dialog>
);

// ─── CorrectionTimeline ───────────────────────────────────────────────────────

export const CorrectionTimeline = ({
  corrections,
  onEdit,
  onHover,
  onFocus,
}: CorrectionTimelineProps) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const { updateRedlineData } = useMapStore();
  const { handleCopy, copied } = useCopy();

  const visible = showAll ? corrections : corrections.slice(0, PREVIEW_COUNT);
  const hiddenCount = corrections.length - PREVIEW_COUNT;

  return (
    <div className="pt-1">
      {/* Timeline rail */}
      <div className="relative">
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

        <div className="space-y-1">
          {visible.map((c, i) => {
            const cfg = STATUS_CONFIG[c.status] ?? STATUS_CONFIG.PENDING;
            const StatusIcon = cfg.icon;
            const isExpanded = expanded === c.id;
            const isLatest = i === 0;

            return (
              <div key={c.id} className="relative flex gap-3">
                {/* Timeline dot */}
                <div className="relative z-10 mt-2.5 flex-shrink-0">
                  <div
                    className={cn(
                      'w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center',
                      cfg.bg,
                      isLatest ? 'border-primary/30' : 'border-border',
                    )}
                  >
                    <StatusIcon className="w-3 h-3" />
                  </div>
                </div>

                {/* Card */}
                <div
                  className={cn(
                    'flex-1 mb-1 rounded-lg border bg-card transition-shadow',
                    isExpanded && 'shadow-sm',
                  )}
                  onMouseEnter={() => onHover?.(c.id)}
                  onMouseLeave={() => onHover?.(null)}
                >
                  {/* Collapsed row */}
                  <button
                    type="button"
                    onClick={() => setExpanded(isExpanded ? null : c.id)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left min-w-0"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-[10px] font-mono font-medium px-1.5 py-px rounded bg-muted text-muted-foreground border border-border/60 shrink-0 whitespace-nowrap">
                        {c.correctionType}
                      </span>
                      <span className="text-[11px] text-muted-foreground shrink-0 whitespace-nowrap">
                        {new Date(c.createdAt).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {c.submittedBy && (
                        <span className="text-[11px] text-muted-foreground truncate min-w-0">
                          · {c.submittedBy.email.split('@')[0]}
                        </span>
                      )}
                    </div>
                    <ChevronDown
                      className={cn(
                        'w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform duration-150',
                        isExpanded && 'rotate-180',
                      )}
                    />
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2 border-t border-border/50">
                      {/* Status + submitter */}
                      <div className="flex items-center gap-2 pt-2.5 flex-wrap">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] h-5 px-2 gap-1 font-medium rounded-full shrink-0',
                            cfg.bg,
                          )}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {c.status}
                        </Badge>
                        {c.submittedBy && (
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <User className="w-3 h-3 shrink-0" />
                            <span className="truncate">
                              {c.submittedBy.email.split('@')[0]}
                            </span>
                          </span>
                        )}
                      </div>

                      {/* Review notes */}
                      {c.reviewNotes && (
                        <div className="rounded-md bg-muted/40 border border-border/50 px-3 py-2 space-y-1">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                            Review Notes
                          </p>
                          <p className="text-xs text-foreground leading-relaxed">
                            {c.reviewNotes}
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between gap-2 pt-0.5 border-t border-border/50 min-w-0">
                        <div className="min-w-0 flex-1">
                          <GeoJsonPreview
                            data={c.proposedGeom}
                            onCopy={handleCopy}
                            copied={copied}
                          />
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {c.proposedGeom && onEdit && (
                            <button
                              type="button"
                              onClick={() => onEdit(c)}
                              className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/70 transition-colors whitespace-nowrap"
                            >
                              <Eye className="w-3 h-3" />
                              View/Edit
                            </button>
                          )}

                          {/* {c.proposedGeom && onFocus && (
                            <button
                              type="button"
                              onClick={() => onFocus(c)}
                              className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/70 transition-colors whitespace-nowrap"
                            >
                              <Eye className="w-3 h-3" />
                              Focus on map
                            </button>
                          )} */}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Show more / less */}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((p) => !p)}
          className="mt-2 ml-[34px] text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        >
          {showAll ? (
            <>
              <ChevronsUp className="w-3 h-3" /> Show less
            </>
          ) : (
            <>
              <ChevronsDown className="w-3 h-3" /> Show {hiddenCount} more
            </>
          )}
        </button>
      )}
    </div>
  );
};
